// http.js
import axios from "axios";
import router from "../router";
import { pinia } from "@/pinia";
import { useLoadingStore } from "@/stores/loadingStore";
import { useIsEditStore } from "@/stores/isEditStore";
import { beginUnauthorizedRedirect } from "@/request/unauthorizedRedirectState";

export const api = axios.create({
  baseURL: process.env.VUE_APP_API_URL,
  timeout: 600000,
});

// 確保每個 request 只會被計數一次
function trackLoading(config) {
  // skipLoading 就不要追蹤
  if (config?.meta?.skipLoading) return config;

  // 已追蹤過就不要再 +1（防止某些情況重複走 request interceptor）
  if (!config.__loadingTracked) {
    config.__loadingTracked = true;

    const store = useLoadingStore(pinia);
    store.show();
    config.__loadingCancelHandler = () => untrackLoading(config);
    if (config.cancelToken?.reason || config.signal?.aborted) {
      config.__loadingCancelHandler();
    } else {
      config.cancelToken?.subscribe?.(config.__loadingCancelHandler);
      config.signal?.addEventListener?.("abort", config.__loadingCancelHandler, {
        once: true,
      });
    }

    // console.log(
    //   "[Loading +]",
    //   "id:",
    //   store.$id,
    //   "count:",
    //   store.activeRequests,
    //   config.method,
    //   config.url
    // );
  }
  return config;
}

function untrackLoading(config) {
  if (!config) return;
  if (config.meta?.skipLoading) return;

  // 只在 tracked 時 -1，並且立刻關掉旗標，避免重複 -1 或漏 -1
  if (config.__loadingTracked) {
    config.__loadingTracked = false;

    if (config.__loadingCancelHandler) {
      config.cancelToken?.unsubscribe?.(config.__loadingCancelHandler);
      config.signal?.removeEventListener?.("abort", config.__loadingCancelHandler);
      delete config.__loadingCancelHandler;
    }

    const store = useLoadingStore(pinia);
    store.hide();

    // console.log(
    //   "[Loading -]",
    //   "id:",
    //   store.$id,
    //   "count:",
    //   store.activeRequests,
    //   config.method,
    //   config.url
    // );
  }
}

// 請求攔截器
api.interceptors.request.use(
  (config) => {
    config = trackLoading(config);

    try {
      if (localStorage.token && !config.meta?.skipAuthInterceptor) {
        config.headers.Authorization = `Bearer ${localStorage.getItem("token")}`;
      }
    } catch (error) {
      untrackLoading(config);
      return Promise.reject(error);
    }

    return config;
  },
  (error) => {
    untrackLoading(error.config);
    return Promise.reject(error);
  }
);

// 回應攔截器
api.interceptors.response.use(
  (response) => {
    untrackLoading(response.config);
    return response;
  },
  (error) => {
    untrackLoading(error.config);

    const config = error.config || {};
    const isLoginAPI = config?.meta?.skipAuthInterceptor === true;
    const shouldSkipUnauthorizedRedirect =
      config?.meta?.skipUnauthorizedRedirect === true;

    if (error.response && error.response.status === 401) {
      if (isLoginAPI || shouldSkipUnauthorizedRedirect) {
        return Promise.reject(error.response);
      } else {
        if (error.response.data?.Code === "IP_NOT_ALLOWED") {
          sessionStorage.setItem(
            "loginErrorMessage",
            "目前網路環境不允許登入。"
          );
        }
        const finishUnauthorizedRedirect = beginUnauthorizedRedirect();
        localStorage.removeItem("token");
        try {
          Promise.resolve(router.replace({ path: "/login" }))
            .catch(() => undefined)
            .finally(finishUnauthorizedRedirect);
        } catch (routerError) {
          finishUnauthorizedRedirect();
        }
        return Promise.reject(error.response);
      }
    }

    if (error.response) {
      if (error.response.status === 404 && !error.response.data?.Message) {
        const responseData = error.response.data;
        error.response.data = {
          ...(responseData !== null && typeof responseData === "object"
            ? responseData
            : {}),
          Message: "查無資料",
        };
      }
      return Promise.reject(error.response);
    } else if (error.message) {
      return Promise.reject({ data: { Message: error.message } });
    } else {
      return Promise.reject(error);
    }
  }
);

function sanitizeParams(params) {
  if (params instanceof FormData || params instanceof File) return params;
  if (Array.isArray(params)) return params.map(sanitizeParams);
  if (params !== null && typeof params === "object") {
    const result = {};
    for (const key in params) {
      result[key] = sanitizeParams(params[key]);
    }
    return result;
  }
  return params === "" ? null : params;
}

export function httpGet(url, params, config = {}) {
  config.params = params;
  return api.get(url, config).then((res) => res.data);
}

export function httpPost(url, params, config = {}) {
  const sanitized = config.meta?.preserveEmptyStrings
    ? params
    : sanitizeParams(params);
  return api.post(url, sanitized, config).then((res) => {
    useIsEditStore().setIsEdit(false);
    return res.data;
  });
}

export function httpPut(url, params, config = {}) {
  const sanitized = config.meta?.preserveEmptyStrings
    ? params
    : sanitizeParams(params);
  return api.put(url, sanitized, config).then((res) => res.data);
}

export function httpPatch(url, params, config = {}) {
  const sanitized = sanitizeParams(params);
  return api.patch(url, sanitized, config).then((res) => res.data);
}

export function httpDelete(url, params = {}, config = {}) {
  config.params = params;
  return api.delete(url, config).then((res) => res.data);
}
